import { useEffect, useRef } from 'react';

interface UseBarcodeScannerOptions {
  onScan: (code: string) => void;
  minLength?: number;
  scanTimeout?: number;
  terminator?: string; // e.g., "Enter" or "Tab"
}

/**
 * Detects barcode scanner input by monitoring fast keyboard events.
 * Works with most USB and Bluetooth scanners that act as keyboards.
 */
export function useBarcodeScanner({
  onScan,
  minLength = 6,
  scanTimeout = 50,
  terminator = 'Enter',
}: UseBarcodeScannerOptions) {
  const bufferRef = useRef<string>('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const firstCharTimeRef = useRef<number>(0);

  useEffect(() => {
    const reset = () => {
      bufferRef.current = '';
      firstCharTimeRef.current = 0;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea (not a scanner)
      const target = e.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Treat the terminator key as scan completion
      if (e.key === terminator) {
        const code = bufferRef.current.trim();

        if (code.length >= minLength) {
          // This is a valid scan, prevent the Enter key from doing anything else
          e.preventDefault();
          e.stopPropagation();
          reset();
          onScan(code);
        } else {
          reset();
        }
        return;
      }

      // Only capture printable characters when not typing in an input field
      if (e.key.length === 1 && !isInputField) {
        const now = Date.now();
        if (!firstCharTimeRef.current) firstCharTimeRef.current = now;
        bufferRef.current += e.key;

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          const code = bufferRef.current.trim();
          const duration = Date.now() - firstCharTimeRef.current;
          reset();

          // Heuristic: scanner input = many chars very fast
          if (code.length >= minLength && duration < minLength * 60) {
            onScan(code);
          }
        }, scanTimeout);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      reset();
    };
  }, [onScan, minLength, scanTimeout, terminator]);
}
